import { Loader, Select } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

type Option = { value: string; label: string; isNew?: boolean };

interface AsyncSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null, option?: Option) => void;
  fetchData: (query?: string) => Promise<Option[]>;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  allowCreate?: boolean;
  onCreateNew?: (option: Option) => void;
}

export function AsyncSelect({
  label,
  value,
  onChange,
  fetchData,
  placeholder = "Pick value",
  disabled = false,
  clearable = true,
  allowCreate = false,
  onCreateNew,
}: AsyncSelectProps) {
  const [data, setData] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debounceRef = useRef<number | null>(null);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      fetchData(search)
        .then((res) => setData(res || []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search, fetchData, opened]);

  const handleOpen = () => {
    setOpened(true);
  };

  const handleClose = () => {
    setOpened(false);
    const selected = data.find((o) => o.value === value);
    setSearch(selected?.label ?? "");
  };

  const handleChange = (val: string | null) => {
    if (val === null) {
      onChange(null, undefined);
      setSearch("");
      setLoading(true);
      fetchData("")
        .then((res) => setData(res || []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
      return;
    }
    if (allowCreate && val.startsWith("__create__::")) {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setLoading(false);
      const label = val.replace("__create__::", "");
      const newOpt: Option = { value: label, label, isNew: true };
      setData((prev) => [newOpt, ...prev]);
      setSearch(label);
      onChange(newOpt.value, newOpt);
      if (onCreateNew) onCreateNew(newOpt);
      return;
    }
    const opt = data.find((o) => o.value === val);
    onChange(val, opt);
  };

  const handleSearchChange = (q: string) => {
    setSearch(q);
    if (opened && value !== null) {
      onChange(null, undefined);
    }
  };

  return (
    <Select
      label={label}
      searchable
      clearable={clearable}
      data={(() => {
        const showCreate =
          allowCreate &&
          search.trim().length > 0 &&
          !data.some((o) => o.label === search || o.value === search);
        const createItem = showCreate
          ? [{ value: `__create__::${search}`, label: `+ Create "${search}"` }]
          : [];
        return [...createItem, ...data];
      })()}
      value={value}
      onChange={handleChange}
      searchValue={search}
      onSearchChange={handleSearchChange}
      onDropdownOpen={handleOpen}
      onDropdownClose={handleClose}
      rightSection={loading ? <Loader size={18} /> : undefined}
      placeholder={placeholder}
      disabled={disabled}
      comboboxProps={{ withinPortal: false }}
    />
  );
}
